import {
  AutoIdGenerator,
  getBrandDetails,
  getCategory1Details,
  getCategory2Details,
  getCategory3Details,
  getCategory4Details,
  getLengthsDetails,
  getLicensorDetails,
  getPCTypesDetails,
  getProductStatusDetails,
  getSampleTypeGroupDetails,
  getSeasonsDetails,
  getMultiCustomAttribute,
  getCustomAttributeFreeText,
  getFilterSeasonsDetails,
} from '../Pages/SystemMasters/Store/SystemMasters.action';
import { getSizeRangesMasterDetails } from '../Pages/SystemMasters/Store/SizeRangeMaster.action';
import { fetchBomReferenceSupplier } from '../Pages/Library/BomLibrary/store/BomLibrary.action';
import { fetchColorListLibrary, fetchColorGroupLibrary } from '../Pages/Library/ColorLibary/store/ColorLibrary.action';
import { buildMasterKey, isMasterFresh, markMasterFetched, type MasterParams } from './master-cache';

/** The store slices the registry selectors read from. */
export interface MasterRootState {
  SystemMastersDetails?: Record<string, unknown>;
  BomLibraryDetails?: Record<string, unknown>;
  ColorLibraryDetails?: Record<string, unknown>;
}

/** A redux-thunk dispatch, untyped because the surrounding store is still classic JS Redux. */
export type MasterDispatch = (action: unknown) => unknown;

/** One registry entry: the thunk that loads a master and the selector that reads it back. */
export interface MasterRegistryEntry {
  thunk: (params?: MasterParams) => unknown;
  select: (state: MasterRootState) => unknown;
}

/** Options accepted by {@link fetchMaster}. */
export interface FetchMasterOptions {
  force?: boolean;
  ttlMs?: number;
}

/** Master reads currently in flight, so concurrent callers share one request. */
const inFlightMasters = new Map<string, Promise<unknown>>();

/**
 * The store slice each master read left behind, by cache key. Every params variant of a master
 * (e.g. active-only vs all seasons) lands in one shared slice, so a fresh key alone does not mean
 * the slice still holds that read's data — another read, or a thunk dispatched outside the
 * registry, may have replaced it since. A read is only skipped while the slice is still the one it
 * produced.
 */
const loadedSlices = new Map<string, unknown>();

/**
 * Which thunk loads each master, and where its result lands in the store.
 *
 * `select` is what makes the guard self-healing: a fetch is skipped only when the store actually
 * holds data AND that data is fresh, so a failed or empty load is retried on the next mount rather
 * than being cached as "already done".
 *
 * A master missing from this registry is not an error — its call sites simply keep fetching every
 * time, exactly as before.
 */
export const MASTER_REGISTRY: Record<string, MasterRegistryEntry> = {
  brands: { thunk: getBrandDetails, select: (state: MasterRootState) => state?.SystemMastersDetails?.brandList },
  seasons: { thunk: getSeasonsDetails, select: (state: MasterRootState) => state?.SystemMastersDetails?.seasonList },
  pcTypes: { thunk: getPCTypesDetails, select: (state: MasterRootState) => state?.SystemMastersDetails?.pcTypeList },
  productStatus: { thunk: getProductStatusDetails, select: (state: MasterRootState) => state?.SystemMastersDetails?.productStatusList },
  category1: { thunk: getCategory1Details, select: (state: MasterRootState) => state?.SystemMastersDetails?.category1List },
  category2: { thunk: getCategory2Details, select: (state: MasterRootState) => state?.SystemMastersDetails?.category2List },
  category3: { thunk: getCategory3Details, select: (state: MasterRootState) => state?.SystemMastersDetails?.category3List },
  category4: { thunk: getCategory4Details, select: (state: MasterRootState) => state?.SystemMastersDetails?.category4List },
  lengths: { thunk: getLengthsDetails, select: (state: MasterRootState) => state?.SystemMastersDetails?.lengthsList },
  licensor: { thunk: getLicensorDetails, select: (state: MasterRootState) => state?.SystemMastersDetails?.licensorList },
  sampleTypeGroup: { thunk: getSampleTypeGroupDetails, select: (state: MasterRootState) => state?.SystemMastersDetails?.sampleTypeGroupList },
  autoIdGenerator: { thunk: AutoIdGenerator, select: (state: MasterRootState) => state?.SystemMastersDetails?.autoIdMaster },
  sizeRanges: { thunk: getSizeRangesMasterDetails, select: (state: MasterRootState) => state?.SystemMastersDetails?.sizeRangesMasterList },
  referenceSupplier: { thunk: fetchBomReferenceSupplier, select: (state: MasterRootState) => state?.BomLibraryDetails?.ReferenceSupplier },
  /*
   * Masters fetched unguarded on the product-information mount until now. Only entries whose
   * params are plain data are listed: several sibling thunks take the i18n `t` function as their
   * argument, and a function cannot serialise into a stable cache key.
   */
  multiCustomAttribute: { thunk: getMultiCustomAttribute, select: (state: MasterRootState) => state?.SystemMastersDetails?.pcpCustomAttribute },
  customAttributeFreeText: { thunk: getCustomAttributeFreeText, select: (state: MasterRootState) => state?.SystemMastersDetails?.customAttributeList },
  colourList: { thunk: fetchColorListLibrary, select: (state: MasterRootState) => state?.ColorLibraryDetails?.ColorWaysLibList },
  colourGroup: { thunk: fetchColorGroupLibrary, select: (state: MasterRootState) => state?.ColorLibraryDetails?.ColorLibGroupList },
  filterSeasons: { thunk: getFilterSeasonsDetails, select: (state: MasterRootState) => state?.SystemMastersDetails?.filterSeasonList },
};

/**
 * Whether a selected slice counts as loaded.
 * @param value the selected slice
 * @returns true when it holds usable data
 */
const hasData = (value: unknown): boolean => (Array.isArray(value) ? value.length > 0 : Boolean(value));

/**
 * Load a registered master, skipping the request when the store copy is present and still fresh.
 *
 * The key is marked before the request goes out, so two components mounting together do not both
 * fetch — which matters for the master reads that travel over POST and therefore cannot be
 * collapsed by the GET de-duplication layer.
 *
 * @param name the registered master name
 * @param params params for the underlying thunk
 * @param options force bypasses the freshness check
 * @returns a thunk resolving to false when the fetch was skipped
 */
export const fetchMaster =
  (name: string, params?: MasterParams, options: FetchMasterOptions = {}) =>
  async (dispatch: MasterDispatch, getState: () => MasterRootState): Promise<unknown> => {
    const entry = MASTER_REGISTRY[name];
    if (!entry) {
      console.error('master-registry: fetchMaster: unknown master:', name);
      return false;
    }
    const key = buildMasterKey(name, params);

    /* Two components mounting together both find an empty store, so freshness alone would not stop
     them both fetching. Joining the request already in flight does — and unlike the transport-level
     GET de-duplication it also covers the masters this API serves over POST, such as categories. */
    const inFlight = inFlightMasters.get(key);
    if (inFlight && !options.force) return inFlight;

    const sliceBefore = entry.select(getState());
    if (!options.force && hasData(sliceBefore) && loadedSlices.get(key) === sliceBefore && isMasterFresh(key, options.ttlMs)) {
      return false;
    }

    const request = Promise.resolve(dispatch(entry.thunk(params)));
    inFlightMasters.set(key, request);
    try {
      return await request;
    } finally {
      inFlightMasters.delete(key);
      /* Recorded only once the fetch has settled, so a request still in flight is never mistaken
       for a completed read. A failure leaves the store empty, and the `hasData` check above then
       makes the next mount retry rather than trusting this timestamp. */
      markMasterFetched(key);
      const sliceAfter = entry.select(getState());
      if (sliceAfter !== sliceBefore) loadedSlices.set(key, sliceAfter);
    }
  };
